import { useEffect, useMemo, useState } from "react";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTheme } from "next-themes";
import {
  CheckCircle2,
  Mail,
  Search,
  Shield,
  Trash2,
  UserCog,
  Users,
  XCircle,
} from "lucide-react";

import { AdminLoginCard } from "@/components/admin/AdminLoginCard";
import { NumerolojiAdminMobileSidebar, NumerolojiAdminSidebar } from "@/components/admin/NumerolojiAdminSidebar";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { adminFetch, useAdminSession } from "@/hooks/use-admin-session";
import { cn } from "@/lib/utils";
import type { MembershipPlanCode } from "../../shared/api";

type KarmaProfile = {
  id: string;
  handle: string;
  display_name: string;
  birth_date: string | null;
  bio: string | null;
  photo_url: string | null;
  match_enabled: boolean;
  created_at: string;
  updated_at: string;
};

type AdminUserListItem = {
  id: string;
  email: string | null;
  created_at: string | null;
  last_sign_in_at: string | null;
  email_confirmed_at: string | null;
  app: {
    karma_profile: KarmaProfile | null;
  };
};

type UsersResponse = {
  ok: true;
  items: AdminUserListItem[];
  page: number;
  perPage: number;
  total: number | null;
};

type MembershipSummary = {
  planCode: MembershipPlanCode;
  isPaid: boolean;
  sourceSubjectType: string | null;
  sourceSubjectId: string | null;
  expiresAt: string | null;
};

type UserDetailResponse = {
  ok: true;
  user: {
    id: string;
    email: string | null;
    created_at: string | null;
    last_sign_in_at: string | null;
    email_confirmed_at: string | null;
    phone: string | null;
    user_metadata: Record<string, unknown>;
  };
  app: {
    karma_profile: KarmaProfile | null;
    stats: {
      likesGiven: number;
      likesReceived: number;
      passesGiven: number;
    };
    membership: MembershipSummary;
  };
};

function fmtDate(value: string | null) {
  if (!value) return "-";
  try {
    return new Date(value).toLocaleString("tr-TR", { dateStyle: "medium", timeStyle: "short" });
  } catch {
    return value;
  }
}

async function fetchUsers(args: { page: number; perPage: number; q: string }): Promise<UsersResponse> {
  const url = new URL("/api/admin/users", window.location.origin);
  url.searchParams.set("page", String(args.page));
  url.searchParams.set("perPage", String(args.perPage));
  if (args.q.trim()) url.searchParams.set("q", args.q.trim());

  const res = await adminFetch(url.pathname + url.search);
  const json = (await res.json().catch(() => null)) as any;
  if (!res.ok) {
    throw new Error(typeof json?.error === "string" ? json.error : "Kullanıcılar alınamadı.");
  }
  return json as UsersResponse;
}

async function fetchUserDetail(id: string): Promise<UserDetailResponse> {
  const res = await adminFetch(`/api/admin/users/${encodeURIComponent(id)}`);
  const json = (await res.json().catch(() => null)) as any;
  if (!res.ok) {
    throw new Error(typeof json?.error === "string" ? json.error : "Kullanıcı detayı alınamadı.");
  }
  return json as UserDetailResponse;
}

export default function AdminUsers() {
  const { setTheme, theme } = useTheme();
  const isDark = theme === "dark";

  const queryClient = useQueryClient();
  const { sessionQuery, loginMutation, logoutMutation } = useAdminSession();
  const authenticated = !!sessionQuery.data?.authenticated;
  const configured = !!sessionQuery.data?.configured;

  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const perPage = 50;

  const [selectedId, setSelectedId] = useState<string | null>(null);

  const usersQuery = useQuery({
    queryKey: ["admin-users", page, perPage, q],
    queryFn: () => fetchUsers({ page, perPage, q }),
    enabled: authenticated,
    staleTime: 15_000,
  });

  const userDetailQuery = useQuery({
    queryKey: ["admin-user", selectedId],
    queryFn: () => {
      if (!selectedId) throw new Error("id gerekli");
      return fetchUserDetail(selectedId);
    },
    enabled: authenticated && !!selectedId,
    staleTime: 10_000,
  });

  const [profileDraft, setProfileDraft] = useState<{
    display_name: string;
    handle: string;
    birth_date: string | null;
    bio: string;
    match_enabled: boolean;
  } | null>(null);
  const [newPassword, setNewPassword] = useState("");
  const [membershipDraft, setMembershipDraft] = useState<MembershipPlanCode>("free");
  const [planType, setPlanType] = useState<"monthly" | "yearly" | "manual">("manual");
  const [paymentSource, setPaymentSource] = useState<"google_play" | "app_store" | "manual">("manual");
  const todayIso = () => new Date().toISOString().slice(0, 10);
  const [startsAt, setStartsAt] = useState(todayIso());
  const [expiresAt, setExpiresAt] = useState("");

  const updatePasswordMutation = useMutation({
    mutationFn: async (args: { userId: string; newPassword: string }) => {
      const res = await adminFetch(`/api/admin/users/${encodeURIComponent(args.userId)}/password`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ newPassword: args.newPassword }),
      });
      const json = (await res.json().catch(() => null)) as any;
      if (!res.ok) {
        throw new Error(typeof json?.error === "string" ? json.error : "Şifre güncellenemedi.");
      }
      return json as { ok: true };
    },
    onSuccess: () => {
      setNewPassword("");
    },
  });

  useEffect(() => {
    setNewPassword("");
    updatePasswordMutation.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId]);

  useEffect(() => {
    if (!selectedId) {
      setProfileDraft(null);
      return;
    }

    const detail = userDetailQuery.data?.ok ? userDetailQuery.data : null;
    const kp = detail?.app.karma_profile ?? null;
    if (!kp) {
      setProfileDraft(null);
      return;
    }

    setProfileDraft({
      display_name: kp.display_name,
      handle: kp.handle,
      birth_date: kp.birth_date,
      bio: kp.bio ?? "",
      match_enabled: !!kp.match_enabled,
    });
  }, [selectedId, userDetailQuery.data]);

  useEffect(() => {
    const detail = userDetailQuery.data?.ok ? userDetailQuery.data : null;
    const plan = detail?.app.membership?.planCode;
    if (plan === "premium" || plan === "free") {
      setMembershipDraft(plan);
      return;
    }
    setMembershipDraft("free");
  }, [userDetailQuery.data]);


  const confirmEmailMutation = useMutation({
    mutationFn: async (userId: string) => {
      const res = await adminFetch(`/api/admin/users/${encodeURIComponent(userId)}/confirm-email`, {
        method: "POST",
      });
      const json = (await res.json().catch(() => null)) as any;
      if (!res.ok) {
        throw new Error(typeof json?.error === "string" ? json.error : "Email doğrulanamadı.");
      }
      return json as { ok: true };
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["admin-users"] });
      void queryClient.invalidateQueries({ queryKey: ["admin-user", selectedId] });
    },
  });

  const updateKarmaProfileMutation = useMutation({
    mutationFn: async (args: { userId: string; patch: Partial<KarmaProfile> }) => {
      const res = await adminFetch(`/api/admin/users/${encodeURIComponent(args.userId)}/karma-profile`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(args.patch),
      });
      const json = (await res.json().catch(() => null)) as any;
      if (!res.ok) {
        throw new Error(typeof json?.error === "string" ? json.error : "Profil güncellenemedi.");
      }
      return json as { ok: true; karma_profile: KarmaProfile | null };
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["admin-users"] });
      void queryClient.invalidateQueries({ queryKey: ["admin-user", selectedId] });
    },
  });

  const updateMembershipMutation = useMutation({
    mutationFn: async (args: { userId: string; planCode: MembershipPlanCode }) => {
      const res = await adminFetch(`/api/admin/users/${encodeURIComponent(args.userId)}/membership`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ planCode: args.planCode }),
      });
      const json = (await res.json().catch(() => null)) as any;
      if (!res.ok) {
        throw new Error(typeof json?.error === "string" ? json.error : "Üyelik planı güncellenemedi.");
      }
      return json as { ok: true; membership: MembershipSummary };
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["admin-user", selectedId] });
      void queryClient.invalidateQueries({ queryKey: ["admin-users"] });
    },
  });

  const deleteUserMutation = useMutation({
    mutationFn: async (userId: string) => {
      const res = await adminFetch(`/api/admin/users/${encodeURIComponent(userId)}/delete`, {
        method: "POST",
      });
      const json = (await res.json().catch(() => null)) as any;
      if (!res.ok) {
        throw new Error(typeof json?.error === "string" ? json.error : "Kullanıcı silinemedi.");
      }
      return json as { ok: true };
    },
    onSuccess: () => {
      setSelectedId(null);
      void queryClient.invalidateQueries({ queryKey: ["admin-users"] });
    },
  });

  const items = usersQuery.data?.items ?? [];

  const stats = useMemo(() => {
    const confirmed = items.filter((u) => !!u.email_confirmed_at).length;
    const hasKarma = items.filter((u) => !!u.app.karma_profile).length;
    const matchEnabled = items.filter((u) => u.app.karma_profile?.match_enabled).length;
    return { confirmed, hasKarma, matchEnabled };
  }, [items]);

  if (sessionQuery.isLoading) {
    return <div className="text-sm text-muted-foreground">Yükleniyor...</div>;
  }

  if (!authenticated) {
    return (
      <AdminLoginCard
        configured={configured}
        onLogin={(args) => loginMutation.mutate(args)}
        isLoading={loginMutation.isPending}
        error={loginMutation.isError ? (loginMutation.error as Error).message : undefined}
      />
    );
  }

  const detail = userDetailQuery.data?.ok ? userDetailQuery.data : null;
  const karmaProfile = detail?.app.karma_profile ?? null;

  const resetDraft = () => {
    if (!karmaProfile) return;
    setProfileDraft({
      display_name: karmaProfile.display_name,
      handle: karmaProfile.handle,
      birth_date: karmaProfile.birth_date,
      bio: karmaProfile.bio ?? "",
      match_enabled: !!karmaProfile.match_enabled,
    });
  };

  const saveDraft = () => {
    if (!detail || !karmaProfile || !profileDraft) return;

    const patch: Record<string, unknown> = {};

    if (profileDraft.display_name.trim() !== karmaProfile.display_name) {
      patch.display_name = profileDraft.display_name.trim();
    }

    if (profileDraft.handle.trim() !== karmaProfile.handle) {
      patch.handle = profileDraft.handle.trim();
    }

    const draftBirth = profileDraft.birth_date ? profileDraft.birth_date.trim() : null;
    const currentBirth = karmaProfile.birth_date ? karmaProfile.birth_date.trim() : null;
    if (draftBirth !== currentBirth) {
      patch.birth_date = draftBirth;
    }

    if ((profileDraft.bio ?? "") !== (karmaProfile.bio ?? "")) {
      patch.bio = profileDraft.bio;
    }

    if (!!profileDraft.match_enabled !== !!karmaProfile.match_enabled) {
      patch.match_enabled = !!profileDraft.match_enabled;
    }

    if (!Object.keys(patch).length) return;

    updateKarmaProfileMutation.mutate({
      userId: detail.user.id,
      patch: patch as any,
    });
  };

  const saveMembershipDraft = () => {
    if (!detail) return;

    updateMembershipMutation.mutate({
      userId: detail.user.id,
      planCode: membershipDraft,
      planType: membershipDraft === "free" ? null : planType,
      paymentSource: membershipDraft === "free" ? null : paymentSource,
      startsAt: startsAt ? new Date(startsAt).toISOString() : new Date().toISOString(),
      expiresAt: expiresAt ? new Date(expiresAt).toISOString() : null,
    } as any);
  };

  return (
    <div className="flex min-h-dvh w-full">
      <NumerolojiAdminSidebar mode="links" isDark={isDark} onToggleTheme={() => setTheme(isDark ? "light" : "dark")} />

      <div className="flex min-w-0 flex-1 flex-col bg-slate-100/60 dark:bg-slate-900/30">
        <header className="h-16 border-b border-slate-200/70 bg-white/70 px-4 backdrop-blur dark:border-slate-800/60 dark:bg-slate-950/40 sm:px-6">
          <div className="flex h-full items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <NumerolojiAdminMobileSidebar
                mode="links"
                isDark={isDark}
                onToggleTheme={() => setTheme(isDark ? "light" : "dark")}
              />
              <div className="flex items-center gap-2">
                <Users className="h-5 w-5 text-slate-500 dark:text-slate-300" />
                <div className="text-base font-semibold tracking-tight text-slate-900 dark:text-slate-50 sm:text-lg">Kullanıcı Yönetimi</div>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <Button
                variant="outline"
                className="rounded-full"
                size="sm"
                onClick={() => logoutMutation.mutate()}
                disabled={logoutMutation.isPending}
              >
                Çıkış
              </Button>
            </div>
          </div>
        </header>

        <div className="min-w-0 flex-1 p-4 sm:p-6">
          <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-800 dark:bg-slate-950/40">
            <div className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
              <div>
                <div className="text-2xl font-extrabold tracking-tight text-slate-900 dark:text-white">Kullanıcılar</div>
                <div className="mt-2 max-w-2xl text-sm text-slate-500 dark:text-slate-400">
                  Supabase kullanıcıları + karma profilleri üzerinde temel yönetim işlemleri.
                </div>
              </div>

              <div className="flex flex-wrap gap-3">
                <div className="flex min-w-[160px] items-center gap-3 rounded-2xl border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-950/40">
                  <div className="grid h-10 w-10 place-items-center rounded-xl bg-emerald-500/10 text-emerald-600 dark:text-emerald-300">
                    <CheckCircle2 className="h-5 w-5" />
                  </div>
                  <div>
                    <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">Doğrulanan</div>
                    <div className="text-xl font-extrabold">{stats.confirmed}</div>
                  </div>
                </div>

                <div className="flex min-w-[160px] items-center gap-3 rounded-2xl border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-950/40">
                  <div className="grid h-10 w-10 place-items-center rounded-xl bg-blue-500/10 text-blue-600 dark:text-blue-300">
                    <UserCog className="h-5 w-5" />
                  </div>
                  <div>
                    <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">Karma Profili</div>
                    <div className="text-xl font-extrabold">{stats.hasKarma}</div>
                  </div>
                </div>

                <div className="flex min-w-[160px] items-center gap-3 rounded-2xl border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-950/40">
                  <div className="grid h-10 w-10 place-items-center rounded-xl bg-violet-500/10 text-violet-600 dark:text-violet-300">
                    <Shield className="h-5 w-5" />
                  </div>
                  <div>
                    <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">Eşleşmeye Açık</div>
                    <div className="text-xl font-extrabold">{stats.matchEnabled}</div>
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-6 flex flex-col gap-4 lg:flex-row lg:items-center">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                <input
                  className="h-11 w-full rounded-2xl bg-slate-100 pl-10 pr-4 text-sm text-slate-900 outline-none ring-blue-600/20 focus:ring-4 dark:bg-slate-800 dark:text-slate-50"
                  placeholder="Email / ad / handle ara..."
                  value={q}
                  onChange={(e) => {
                    setPage(1);
                    setQ(e.target.value);
                  }}
                />
              </div>

              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  className={cn("rounded-full", "bg-white dark:bg-slate-950/40")}
                  onClick={() => usersQuery.refetch()}
                  disabled={usersQuery.isFetching}
                >
                  Yenile
                </Button>
              </div>
            </div>

            <div className="mt-6">
              {usersQuery.isLoading ? (
                <div className="text-sm text-muted-foreground">Yükleniyor...</div>
              ) : usersQuery.isError ? (
                <div className="text-sm text-destructive">{(usersQuery.error as Error).message}</div>
              ) : items.length ? (
                <div className="overflow-hidden rounded-2xl border border-slate-200 dark:border-slate-800">
                  <div className="grid grid-cols-[1.2fr_1fr_140px_90px_160px] gap-3 bg-slate-50 px-4 py-3 text-[11px] font-bold uppercase tracking-wider text-slate-500 dark:bg-slate-900/40 dark:text-slate-400">
                    <div>Kullanıcı</div>
                    <div>Profil</div>
                    <div>Email</div>
                    <div>Plan</div>
                    <div>İşlem</div>
                  </div>

                  <div className="divide-y divide-slate-200 dark:divide-slate-800">
                    {items.map((u) => {
                      const kp = u.app.karma_profile;
                      const confirmed = !!u.email_confirmed_at;

                      return (
                        <div
                          key={u.id}
                          className="grid grid-cols-[1.2fr_1fr_140px_90px_160px] gap-3 px-4 py-4 text-sm hover:bg-slate-50 dark:hover:bg-slate-900/30"
                        >
                          <div className="min-w-0">
                            <div className="truncate font-semibold text-slate-900 dark:text-white">{u.email ?? u.id}</div>
                            <div className="mt-1 text-xs text-slate-500 dark:text-slate-400">Son giriş: {fmtDate(u.last_sign_in_at)}</div>
                          </div>

                          <div className="min-w-0">
                            {kp ? (
                              <>
                                <div className="truncate font-semibold text-slate-900 dark:text-white">{kp.display_name}</div>
                                <div className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                                  @{kp.handle} • {kp.match_enabled ? "Eşleşmeye açık" : "Kapalı"}
                                </div>
                              </>
                            ) : (
                              <div className="text-xs text-slate-500 dark:text-slate-400">Karma profili yok</div>
                            )}
                          </div>

                          <div>
                            <div
                              className={cn(
                                "inline-flex items-center gap-2 rounded-full px-3 py-1 text-[11px] font-bold",
                                confirmed
                                  ? "bg-emerald-500/10 text-emerald-700 dark:text-emerald-300"
                                  : "bg-orange-500/10 text-orange-700 dark:text-orange-300",
                              )}
                            >
                              {confirmed ? <CheckCircle2 className="h-4 w-4" /> : <XCircle className="h-4 w-4" />}
                              {confirmed ? "Doğrulandı" : "Bekliyor"}
                            </div>
                          </div>

                          <div className="flex items-center">
                            <span style={{
                              color: (u.app as any).membership?.planCode === 'premium' ? '#a78bfa' : '#6b7280',
                              fontWeight: 'bold',
                              fontSize: 12,
                            }}>
                              {(u.app as any).membership?.planCode === 'premium' ? '⭐ Premium' : 'Ücretsiz'}
                            </span>
                          </div>

                          <div className="flex items-center justify-end gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              className="rounded-full bg-white dark:bg-slate-950/40"
                              onClick={() => setSelectedId(u.id)}
                            >
                              Detay
                            </Button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ) : (
                <div className="text-sm text-slate-500 dark:text-slate-400">Kullanıcı bulunamadı.</div>
              )}
            </div>

            <div className="mt-6 flex items-center justify-between">
              <div className="text-xs text-slate-500 dark:text-slate-400">
                Sayfa {page} • {usersQuery.data?.total ? `Toplam: ${usersQuery.data.total}` : ""}
              </div>

              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  className="rounded-full bg-white dark:bg-slate-950/40"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page <= 1}
                >
                  Geri
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="rounded-full bg-white dark:bg-slate-950/40"
                  onClick={() => setPage((p) => p + 1)}
                  disabled={(usersQuery.data?.items?.length ?? 0) < perPage}
                >
                  İleri
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Dialog open={!!selectedId} onOpenChange={(open) => (!open ? setSelectedId(null) : null)}>
        <DialogContent className="max-h-[85vh] max-w-2xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Kullanıcı Detayı</DialogTitle>
          </DialogHeader>

          {userDetailQuery.isLoading ? (
            <div className="text-sm text-muted-foreground">Yükleniyor...</div>
          ) : userDetailQuery.isError ? (
            <div className="text-sm text-destructive">{(userDetailQuery.error as Error).message}</div>
          ) : detail ? (
            <div className="grid gap-6">
              <div className="grid gap-3 rounded-2xl border bg-muted/20 p-4">
                <div className="grid gap-1">
                  <div className="text-xs font-semibold text-muted-foreground">Email</div>
                  <div className="font-semibold">{detail.user.email ?? "-"}</div>
                </div>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <div className="grid gap-1">
                    <div className="text-xs font-semibold text-muted-foreground">Oluşturulma</div>
                    <div className="text-sm">{fmtDate(detail.user.created_at)}</div>
                  </div>
                  <div className="grid gap-1">
                    <div className="text-xs font-semibold text-muted-foreground">Son giriş</div>
                    <div className="text-sm">{fmtDate(detail.user.last_sign_in_at)}</div>
                  </div>
                </div>

                <div className="flex flex-wrap items-center gap-2">
                  <div
                    className={cn(
                      "inline-flex items-center gap-2 rounded-full px-3 py-1 text-[11px] font-bold",
                      detail.user.email_confirmed_at
                        ? "bg-emerald-500/10 text-emerald-700 dark:text-emerald-300"
                        : "bg-orange-500/10 text-orange-700 dark:text-orange-300",
                    )}
                  >
                    {detail.user.email_confirmed_at ? <CheckCircle2 className="h-4 w-4" /> : <XCircle className="h-4 w-4" />}
                    {detail.user.email_confirmed_at ? "Email doğrulandı" : "Email doğrulanmadı"}
                  </div>

                  {!detail.user.email_confirmed_at ? (
                    <Button
                      size="sm"
                      variant="outline"
                      className="rounded-full"
                      onClick={() => confirmEmailMutation.mutate(detail.user.id)}
                      disabled={confirmEmailMutation.isPending}
                    >
                      <Mail className="mr-2 h-4 w-4" />
                      Email Doğrula
                    </Button>
                  ) : null}
                </div>
              </div>

              <div className="grid gap-3 rounded-2xl border bg-muted/20 p-4">
                <div className="text-sm font-semibold">Şifre Değiştir</div>
                <div className="grid gap-3 sm:grid-cols-[1fr_auto] sm:items-end">
                  <div className="grid gap-2">
                    <Label>Yeni Şifre</Label>
                    <Input
                      type="password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      placeholder="En az 6 karakter"
                      className="h-10"
                    />
                  </div>
                  <Button
                    type="button"
                    size="sm"
                    className="rounded-full"
                    disabled={updatePasswordMutation.isPending || newPassword.trim().length < 6}
                    onClick={() => {
                      if (!window.confirm("Bu kullanıcının şifresini değiştirmek istediğinize emin misiniz?")) return;
                      updatePasswordMutation.mutate({ userId: detail.user.id, newPassword: newPassword.trim() });
                    }}
                  >
                    {updatePasswordMutation.isPending ? "Güncelleniyor..." : "Şifreyi Güncelle"}
                  </Button>
                </div>

                {updatePasswordMutation.isError ? (
                  <div className="text-sm text-destructive">{(updatePasswordMutation.error as Error).message}</div>
                ) : null}
                {updatePasswordMutation.isSuccess ? (
                  <div className="text-sm text-emerald-600 dark:text-emerald-400">Şifre başarıyla güncellendi.</div>
                ) : null}
              </div>

              <div className="grid gap-4">
                <div className="text-sm font-semibold">Karma Profili</div>

                {karmaProfile ? (
                  <div className="grid gap-4 rounded-2xl border p-4">
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                      <div className="grid gap-2">
                        <Label>Ad Soyad</Label>
                        <Input
                          value={profileDraft?.display_name ?? karmaProfile.display_name}
                          onChange={(e) =>
                            setProfileDraft((prev) => ({
                              display_name: e.target.value,
                              handle: prev?.handle ?? karmaProfile.handle,
                              birth_date: prev?.birth_date ?? karmaProfile.birth_date,
                              bio: prev?.bio ?? (karmaProfile.bio ?? ""),
                              match_enabled: prev?.match_enabled ?? !!karmaProfile.match_enabled,
                            }))
                          }
                          className="h-10"
                        />
                      </div>
                      <div className="grid gap-2">
                        <Label>Handle</Label>
                        <Input
                          value={profileDraft?.handle ?? karmaProfile.handle}
                          onChange={(e) =>
                            setProfileDraft((prev) => ({
                              display_name: prev?.display_name ?? karmaProfile.display_name,
                              handle: e.target.value,
                              birth_date: prev?.birth_date ?? karmaProfile.birth_date,
                              bio: prev?.bio ?? (karmaProfile.bio ?? ""),
                              match_enabled: prev?.match_enabled ?? !!karmaProfile.match_enabled,
                            }))
                          }
                          className="h-10"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                      <div className="grid gap-2">
                        <Label>Doğum Tarihi (YYYY-AA-GG)</Label>
                        <Input
                          value={profileDraft?.birth_date ?? karmaProfile.birth_date ?? ""}
                          onChange={(e) =>
                            setProfileDraft((prev) => ({
                              display_name: prev?.display_name ?? karmaProfile.display_name,
                              handle: prev?.handle ?? karmaProfile.handle,
                              birth_date: e.target.value || null,
                              bio: prev?.bio ?? (karmaProfile.bio ?? ""),
                              match_enabled: prev?.match_enabled ?? !!karmaProfile.match_enabled,
                            }))
                          }
                          className="h-10"
                          placeholder="1990-06-01"
                        />
                      </div>
                      <div className="flex items-center justify-between gap-3 rounded-2xl border px-4 py-3">
                        <div>
                          <div className="text-sm font-semibold">Eşleşmeye açık</div>
                          <div className="mt-1 text-xs text-muted-foreground">Keşfet’te gösterilsin</div>
                        </div>
                        <Switch
                          checked={profileDraft?.match_enabled ?? !!karmaProfile.match_enabled}
                          onCheckedChange={(checked) =>
                            setProfileDraft((prev) => ({
                              display_name: prev?.display_name ?? karmaProfile.display_name,
                              handle: prev?.handle ?? karmaProfile.handle,
                              birth_date: prev?.birth_date ?? karmaProfile.birth_date,
                              bio: prev?.bio ?? (karmaProfile.bio ?? ""),
                              match_enabled: checked,
                            }))
                          }
                        />
                      </div>
                    </div>

                    <div className="grid gap-2">
                      <Label>Bio</Label>
                      <Input
                        value={profileDraft?.bio ?? (karmaProfile.bio ?? "")}
                        onChange={(e) =>
                          setProfileDraft((prev) => ({
                            display_name: prev?.display_name ?? karmaProfile.display_name,
                            handle: prev?.handle ?? karmaProfile.handle,
                            birth_date: prev?.birth_date ?? karmaProfile.birth_date,
                            bio: e.target.value,
                            match_enabled: prev?.match_enabled ?? !!karmaProfile.match_enabled,
                          }))
                        }
                        className="h-10"
                      />
                    </div>

                    <div className="flex flex-wrap items-center justify-end gap-2">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="rounded-full"
                        onClick={resetDraft}
                      >
                        Sıfırla
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        className="rounded-full"
                        onClick={saveDraft}
                        disabled={updateKarmaProfileMutation.isPending}
                      >
                        Kaydet
                      </Button>
                    </div>

                    <div className="grid grid-cols-3 gap-3 text-xs">
                      <div className="rounded-2xl border bg-muted/20 p-3">
                        <div className="font-semibold text-muted-foreground">Beğeni (verdi)</div>
                        <div className="mt-1 text-lg font-extrabold text-slate-900 dark:text-white">{detail.app.stats.likesGiven}</div>
                      </div>
                      <div className="rounded-2xl border bg-muted/20 p-3">
                        <div className="font-semibold text-muted-foreground">Beğeni (aldı)</div>
                        <div className="mt-1 text-lg font-extrabold text-slate-900 dark:text-white">{detail.app.stats.likesReceived}</div>
                      </div>
                      <div className="rounded-2xl border bg-muted/20 p-3">
                        <div className="font-semibold text-muted-foreground">Geçti</div>
                        <div className="mt-1 text-lg font-extrabold text-slate-900 dark:text-white">{detail.app.stats.passesGiven}</div>
                      </div>
                    </div>

                    {updateKarmaProfileMutation.isError ? (
                      <div className="text-sm text-destructive">{(updateKarmaProfileMutation.error as Error).message}</div>
                    ) : null}
                  </div>
                ) : (
                  <Card className="border-slate-200 bg-white p-4 text-sm text-slate-600 dark:border-slate-800 dark:bg-slate-950/40 dark:text-slate-400">
                    Bu kullanıcıda karma profili yok. (Keşfet’i açınca otomatik oluşur)
                  </Card>
                )}
              </div>

              <div className="grid gap-3 rounded-2xl border bg-muted/20 p-4">
                <div className="text-sm font-semibold">Üyelik Planı</div>
                <div className="grid gap-3 sm:grid-cols-2">
                  <div className="grid gap-2">
                    <Label>Mevcut Plan</Label>
                    <div className="rounded-lg border bg-background px-3 py-2 text-sm font-semibold uppercase">
                      {detail.app.membership.planCode}
                    </div>
                  </div>

                  <div className="grid gap-2">
                    <Label>Manuel Atama</Label>
                    <select
                      className="h-10 rounded-md border bg-background px-3 text-sm"
                      value={membershipDraft}
                      onChange={(e) => {
                        const value = String(e.target.value ?? "").trim().toLowerCase();
                        if (value === "premium" || value === "free") {
                          setMembershipDraft(value as MembershipPlanCode);
                        }
                      }}
                    >
                      <option value="free">Ücretsiz</option>
                      <option value="premium">Premium</option>
                    </select>
                  </div>
                </div>

                {membershipDraft === "premium" && (
                  <div className="grid gap-3 sm:grid-cols-2">
                    <div className="grid gap-2">
                      <Label>Plan Tipi</Label>
                      <select
                        className="h-10 rounded-md border bg-background px-3 text-sm"
                        value={planType}
                        onChange={(e) => {
                          const v = e.target.value as "monthly" | "yearly" | "manual";
                          setPlanType(v);
                          if (v === "monthly") {
                            const d = new Date(startsAt || new Date());
                            d.setDate(d.getDate() + 30);
                            setExpiresAt(d.toISOString().slice(0, 10));
                          } else if (v === "yearly") {
                            const d = new Date(startsAt || new Date());
                            d.setDate(d.getDate() + 365);
                            setExpiresAt(d.toISOString().slice(0, 10));
                          } else {
                            setExpiresAt("");
                          }
                        }}
                      >
                        <option value="monthly">Aylık</option>
                        <option value="yearly">Yıllık</option>
                        <option value="manual">Manuel</option>
                      </select>
                    </div>

                    <div className="grid gap-2">
                      <Label>Ödeme Kaynağı</Label>
                      <select
                        className="h-10 rounded-md border bg-background px-3 text-sm"
                        value={paymentSource}
                        onChange={(e) => setPaymentSource(e.target.value as "google_play" | "app_store" | "manual")}
                      >
                        <option value="manual">Manuel</option>
                        <option value="google_play">Google Play</option>
                        <option value="app_store">App Store</option>
                      </select>
                    </div>

                    <div className="grid gap-2">
                      <Label>Başlangıç Tarihi</Label>
                      <input
                        type="date"
                        className="h-10 rounded-md border bg-background px-3 text-sm"
                        value={startsAt}
                        onChange={(e) => setStartsAt(e.target.value)}
                      />
                    </div>

                    <div className="grid gap-2">
                      <Label>Bitiş Tarihi</Label>
                      <input
                        type="date"
                        className="h-10 rounded-md border bg-background px-3 text-sm"
                        value={expiresAt}
                        onChange={(e) => setExpiresAt(e.target.value)}
                        placeholder="Boş = süresiz"
                      />
                    </div>
                  </div>
                )}

                <div className="text-xs text-muted-foreground">
                  Kaynak: {detail.app.membership.sourceSubjectType ?? "-"} / {detail.app.membership.sourceSubjectId ?? "-"}
                  {detail.app.membership.expiresAt ? ` • Bitiş: ${fmtDate(detail.app.membership.expiresAt)}` : ""}
                </div>

                <div className="flex justify-end">
                  <Button
                    type="button"
                    size="sm"
                    className="rounded-full"
                    onClick={saveMembershipDraft}
                    disabled={updateMembershipMutation.isPending}
                  >
                    Planı Kaydet
                  </Button>
                </div>

                {updateMembershipMutation.isError ? (
                  <div className="text-sm text-destructive">{(updateMembershipMutation.error as Error).message}</div>
                ) : null}
              </div>

              <div className="flex items-center justify-between gap-3 rounded-2xl border border-red-200 bg-red-50/60 p-4 dark:border-red-500/20 dark:bg-red-950/20">
                <div>
                  <div className="text-sm font-semibold text-red-700 dark:text-red-200">Tehlikeli işlemler</div>
                  <div className="mt-1 text-xs text-red-700/80 dark:text-red-200/80">Kullanıcıyı tamamen siler (auth + profiller).</div>
                </div>

                <Button
                  variant="destructive"
                  onClick={() => deleteUserMutation.mutate(detail.user.id)}
                  disabled={deleteUserMutation.isPending}
                >
                  <Trash2 className="mr-2 h-4 w-4" />
                  Sil
                </Button>
              </div>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
}
